import { type Country, CountrySelect } from "@/components/country-select";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";

interface Address {
	street: string | null;
	city: string | null;
	postal_code: string | null;
	country: string | null;
}

interface AddressErrors {
	street?: string;
	city?: string;
	postal_code?: string;
	country?: string;
}

interface AddressFieldGroupProps {
	value: Address;
	onChange: (address: Address) => void;
	countries: Country[];
	idPrefix?: string;
	invalid?: boolean;
	errors?: AddressErrors;
	onClearError?: (field: keyof AddressErrors) => void;
}

function AddressFieldGroup({
	value,
	onChange,
	countries,
	idPrefix = "",
	invalid = false,
	errors,
	onClearError,
}: AddressFieldGroupProps) {
	const prefix = idPrefix ? `${idPrefix}_` : "";

	return (
		<FieldGroup>
			<Field
				data-warning={invalid && !value.street}
				data-invalid={!!errors?.street}
			>
				<FieldLabel htmlFor={`${prefix}street`}>
					Ulice a číslo popisné
				</FieldLabel>
				<Input
					id={`${prefix}street`}
					value={value.street || ""}
					onChange={(e) => {
						onClearError?.("street");
						onChange({
							...value,
							street: e.target.value || null,
						});
					}}
					placeholder="Ulice 123/4"
					data-warning={invalid && !value.street}
					aria-invalid={!!errors?.street}
				/>
				<FieldError>{errors?.street}</FieldError>
			</Field>
			<div className="flex gap-4">
				<Field
					className="w-full"
					data-warning={invalid && !value.city}
					data-invalid={!!errors?.city}
				>
					<FieldLabel htmlFor={`${prefix}city`}>Město</FieldLabel>
					<Input
						id={`${prefix}city`}
						value={value.city || ""}
						onChange={(e) => {
							onClearError?.("city");
							onChange({
								...value,
								city: e.target.value || null,
							});
						}}
						placeholder="Praha"
						data-warning={invalid && !value.city}
						aria-invalid={!!errors?.city}
					/>
					<FieldError>{errors?.city}</FieldError>
				</Field>
				<Field
					className="w-68"
					data-warning={invalid && !value.postal_code}
					data-invalid={!!errors?.postal_code}
				>
					<FieldLabel htmlFor={`${prefix}postal_code`}>PSČ</FieldLabel>
					<Input
						id={`${prefix}postal_code`}
						value={value.postal_code || ""}
						onChange={(e) => {
							onClearError?.("postal_code");
							onChange({
								...value,
								postal_code: e.target.value || null,
							});
						}}
						placeholder="110 00"
						data-warning={invalid && !value.postal_code}
						aria-invalid={!!errors?.postal_code}
					/>
					<FieldError>{errors?.postal_code}</FieldError>
				</Field>
				<Field
					className="w-68"
					data-warning={invalid && !value.country}
					data-invalid={!!errors?.country}
				>
					<FieldLabel htmlFor={`${prefix}country`}>Země</FieldLabel>
					<CountrySelect
						value={value.country || ""}
						onChange={(country) => {
							onClearError?.("country");
							onChange({
								...value,
								country,
							});
						}}
						countries={countries}
						data-warning={invalid && !value.country}
					/>
					<FieldError>{errors?.country}</FieldError>
				</Field>
			</div>
		</FieldGroup>
	);
}

export {
	AddressFieldGroup,
	type Address,
	type AddressErrors,
	type AddressFieldGroupProps,
};
